/**
 * useToast Composable - Laumeris
 * Gestion des notifications toast
 */

import { ref, readonly } from 'vue'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  duration?: number
}

type ToastInput = Omit<Toast, 'id'>

// State (singleton)
const toasts = ref<Toast[]>([])

// Generate unique ID
const generateId = (): string => {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function useToast() {
  // Add toast
  const addToast = (toast: ToastInput): string => {
    const id = generateId()
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000
    }

    toasts.value.push(newToast)

    // Auto-remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }

    return id
  }

  // Remove toast
  const removeToast = (id: string): void => {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  // Clear all toasts
  const clearToasts = (): void => {
    toasts.value = []
  }

  // Convenience methods
  const success = (message: string, title?: string, duration?: number): string => {
    return addToast({ type: 'success', message, title, duration })
  }

  const error = (message: string, title?: string, duration?: number): string => {
    return addToast({ type: 'error', message, title, duration: duration ?? 8000 })
  }

  const warning = (message: string, title?: string, duration?: number): string => {
    return addToast({ type: 'warning', message, title, duration })
  }

  const info = (message: string, title?: string, duration?: number): string => {
    return addToast({ type: 'info', message, title, duration })
  }

  // Promise-based toast (for async operations)
  const promise = async <T>(
    promiseFn: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: Error) => string)
    }
  ): Promise<T> => {
    const loadingId = addToast({
      type: 'info',
      message: messages.loading,
      duration: 0 // Don't auto-remove
    })

    try {
      const result = await promiseFn
      removeToast(loadingId)
      success(
        typeof messages.success === 'function'
          ? messages.success(result)
          : messages.success
      )
      return result
    } catch (err) {
      removeToast(loadingId)
      error(
        typeof messages.error === 'function'
          ? messages.error(err as Error)
          : messages.error
      )
      throw err
    }
  }

  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    warning,
    info,
    promise
  }
}
